<template>
    <ion-item lines="none" class="custom-textarea">
        <ion-label v-if="$slots.default" class="fixed-label-width">
            <slot>
                {{ name }}
            </slot>
        </ion-label>

        <ion-textarea
            :value="modelValue"
            label=""
            :auto-grow="props.autoGrow"
            :autocapitalize="props.autocapitalize"
            :autofocus="props.autofocus"
            :clear-on-edit="props.clearOnEdit"
            :color="props.color"
            :class="textareaClass"
            :cols="props.cols"
            :debounce="props.debounce"
            :disabled="props.disabled"
            :enterkeyhint="props.enterkeyhint"
            :inputmode="props.inputmode"
            :maxlength="props.maxlength"
            :minlength="props.minlength"
            :mode="props.mode"
            :name="props.name"
            :placeholder="props.placeholder"
            :readonly="props.readonly"
            :required="props.required"
            :rows="props.rows"
            :spellcheck="props.spellcheck"
            :wrap="props.wrap"
            @ionInput="emit('update:modelValue', $event.detail.value); handleChange($event);"
            @ionBlur="handleBlur($event); emit('blur', $event)"
            @ionChange="emit('change', $event)"
            @ionFocus="emit('focus', $event)"
        />

        <ion-icon
            v-if="validateOption?.options && validateOption.options.reset"
            slot="end"
            :icon="closeCircleOutline"
            @click="resetField(); $emit('update:modelValue', value)"
        />

        <ion-label slot="error" class="base-input-error">
            <span v-for="error of errors" :key="error">
                {{ error }}
            </span>
        </ion-label>

        <ion-label v-if="$slots['helper']" slot="helper">
            <slot name="helper"/>
        </ion-label>
    </ion-item>
</template>

<script setup lang="ts">
import { IonIcon, IonItem, IonLabel, IonTextarea } from "@ionic/vue";
import { useField } from "vee-validate";

import { BaseTextareaProps } from "@/types/props";
import { TextareaBaseEmits } from "@/types/emits";

import { closeCircleOutline } from "ionicons/icons";

const props = withDefaults(defineProps<BaseTextareaProps>(), {
    rows: 4
})

const emit = defineEmits<TextareaBaseEmits>()

// Vee-Validate Composable
const {
    name,
    value,
    errors,
    handleChange,
    handleBlur,
    resetField,
    validate
} = useField(
    props.validateOption?.name ?? '',
    props.validateOption?.rules ?? '',
    {
        valueProp: props.modelValue,
        initialValue: JSON.parse(JSON.stringify(props.modelValue ?? '')),
        label: props.validateOption?.label ?? props.validateOption?.name,
        ...props.validateOption?.options ?? {}
    }
)

window.addEventListener('change-language', () => validate({ mode: 'force' }))
</script>

<style scoped>
.fixed-label-width {
    width: var(--input-label-width, auto);
}

.custom-textarea::part(native) {
    padding-left: 10px;
}

ion-textarea label div .label-text-wrapper {
    width: 200px;
    height: 200px;
    margin-inline-end: 0 !important;
    margin: 0 !important;
}
</style>
